import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

// The business's face, wherever the app shows it: the operator sidebar, the
// sign-in screen, the guest booking header. One definition so a tenant's logo
// cannot appear at three different sizes and shapes on three screens.
//
// Deliberately takes its values as props rather than reading BrandingContext.
// The sign-in screen renders BEFORE any tenant is resolved and therefore outside
// the provider, and it is the surface white labelling matters most on — a
// component that could only work inside the provider would be unusable exactly
// where it is needed.

interface BrandMarkProps {
  /** Business name — supplies the initial for the fallback tile. */
  name: string | null | undefined
  logoUrl: string | null | undefined
  /** Brand primary colour, used only by the fallback tile. */
  color: string
  /** Rendered size in px. */
  size?: number
  /** Logos read as marks; the sign-in screen wants a softened square instead. */
  shape?: 'circle' | 'squircle'
  className?: string
}

export function BrandMark({
  name,
  logoUrl,
  color,
  size = 32,
  shape = 'circle',
  className,
}: BrandMarkProps) {
  // brand_logo_url is a free-text field an operator can point anywhere, so the
  // URL may 404, be hotlink-blocked, or have been deleted since it was saved.
  // Without this the app renders the browser's broken-image glyph in its own
  // chrome — visibly more broken than having no logo at all.
  const [failed, setFailed] = useState(false)

  // A branding change (or a tenant switch on the slug entry) must get a fresh
  // attempt; without this reset the mark stays fallen-back for the session.
  useEffect(() => { setFailed(false) }, [logoUrl])

  const radius = shape === 'circle' ? 'rounded-full' : 'rounded-[22%]'
  const initial = (name ?? '?').trim().charAt(0).toUpperCase() || '?'

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        // Decorative: every caller renders the business name in adjacent text,
        // so a filled alt would make a screen reader announce it twice.
        alt=""
        width={size}
        height={size}
        onError={() => setFailed(true)}
        // object-contain, never cover: cover crops, and a wordmark that is wider
        // than it is tall loses its ends. The light plate is what keeps a logo
        // drawn for white paper legible on the dark nav rail.
        className={cn('shrink-0 bg-white object-contain', radius, className)}
        style={{ width: size, height: size }}
      />
    )
  }

  return (
    <div
      aria-hidden
      className={cn('flex shrink-0 items-center justify-center font-bold text-white', radius, className)}
      style={{ width: size, height: size, backgroundColor: color, fontSize: Math.round(size * 0.44) }}
    >
      {initial}
    </div>
  )
}
